import { resolveSafeNextPath } from '@/lib/auth/safe-next-path'
import { resolvePostLoginDestination } from '@/lib/auth/postLoginDestination'

export type ImplicitAuthCallback = {
  kind: 'implicit'
  access_token: string
  refresh_token: string
  type: string | null
}

export type PkceAuthCallback = {
  kind: 'pkce'
  code: string
  next: string | null
  type: string | null
}

export type OtpAuthCallback = {
  kind: 'otp'
  token_hash: string
  type: string
  next: string | null
}

export type ParsedAuthCallback = ImplicitAuthCallback | PkceAuthCallback | OtpAuthCallback

/**
 * Detects Supabase auth redirect payloads in the current URL.
 * Email templates / Site URL misconfig often land implicit `#access_token…`
 * fragments on `/login` instead of `/auth/confirm?code=…`.
 *
 * Fragments are never sent to the server — only the browser can read them.
 */
export function parseAuthCallbackFromLocation(location: {
  hash: string
  search: string
}): ParsedAuthCallback | null {
  const hash = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash
  if (hash) {
    const params = new URLSearchParams(hash)
    const access_token = params.get('access_token')
    const refresh_token = params.get('refresh_token')
    if (access_token && refresh_token) {
      return {
        kind: 'implicit',
        access_token,
        refresh_token,
        type: params.get('type'),
      }
    }
  }

  const search = location.search.startsWith('?') ? location.search.slice(1) : location.search
  if (search) {
    const params = new URLSearchParams(search)
    const code = params.get('code')
    if (code) {
      return {
        kind: 'pkce',
        code,
        next: params.get('next'),
        type: params.get('type'),
      }
    }

    const token_hash = params.get('token_hash')
    const type = params.get('type')
    if (token_hash && type) {
      return {
        kind: 'otp',
        token_hash,
        type,
        next: params.get('next'),
      }
    }
  }

  return null
}

const AUTH_CALLBACK_SHELL_DESTINATIONS = {
  '/reset-password': '/reset-password',
  '/setup-account': '/setup-account',
} as const

type AuthCallbackShellDestination = keyof typeof AUTH_CALLBACK_SHELL_DESTINATIONS

function resolveAuthCallbackNextPath(next: string): string {
  const safe = resolveSafeNextPath(next, '/')
  const pathname = safe.split(/[?#]/, 1)[0] ?? '/'
  const shellDestination =
    AUTH_CALLBACK_SHELL_DESTINATIONS[pathname as AuthCallbackShellDestination]
  if (shellDestination) return shellDestination
  return resolvePostLoginDestination(safe)
}

/**
 * Where to send the user after a successful auth email callback.
 * Recovery always goes to reset-password; invite/signup to setup-account.
 *
 * Unlike post-login `next=`, email callbacks may intentionally target auth shells
 * (e.g. PKCE `?code=…&next=/reset-password` from forgot-password).
 */
export function resolveAuthCallbackDestination(input: {
  type?: string | null
  next?: string | null
  /** Current path when the callback is consumed — used when Supabase strips type/next. */
  pathname?: string
}): string {
  const type = input.type?.toLowerCase() ?? ''
  if (type === 'recovery') return '/reset-password'
  if (type === 'invite' || type === 'signup') return '/setup-account'

  if (input.next) {
    return resolveAuthCallbackNextPath(input.next)
  }

  const pathname = input.pathname?.split(/[?#]/, 1)[0] ?? ''
  if (pathname === '/reset-password') return '/reset-password'
  if (pathname === '/setup-account') return '/setup-account'
  // Legacy forgot-password PKCE when Supabase drops ?next=/reset-password from redirectTo.
  if (pathname === '/auth/confirm') return '/reset-password'

  return '/'
}
